import { createAction } from '@reduxjs/toolkit';
import moment, { MomentInput } from 'moment';
import { QueryClient } from 'react-query';
import { FieldValue, FieldValueType, Task, Task as TaskApi } from 'api/tasks';
import { Actor } from 'data/core';
import { DateShiftUpdates, EntityType } from 'utils/date-shift';
import {
  UPDATE_TASK_SAGA,
  UPDATE_TASK_DATES_SAGA,
  FETCH_TASK_SAGA,
  CREATE_TASK_SAGA,
  DELETE_TASK_SAGA,
  DELETE_SUB_TASK_SAGA,
  CREATE_SUB_TASK_SAGA,
  SAVE_TASK_SAGA,
  CREATE_DEPENDENCY_SAGA,
  DEPENDENCY_CREATED_SAGA,
  DEPENDENCY_REMOVED_SAGA,
  UPDATE_TASK_TIMELINE_SAGA,
  CREATE_MULTIPLE_SUB_TASKS_SAGA,
  REMOVE_AS_SUBTASK,
  REMOVE_AS_SUBTASK_SUCCESS,
  DEPENDENCIES_REMOVED_SAGA,
  UPDATE_SPRINT_SAGA,
  RESET_ENTITY_DATES,
  UPDATE_TASK_RECORD_WITH_LATEST_DATA,
} from './types';

export interface FetchTaskActionPayload {
  taskId: number;
  projectId: number;
  fetchInBackground?: boolean;
  onSuccess?: (task: Task | undefined) => void;
}

export interface CreateTaskActionPayload {
  projectId: number;
  task: Omit<Partial<Task>, 'projectPhase'>;
}

export interface UpdateTaskActionPayloadOptions {
  resolveCallback?: (value) => void;
  skipStoreUpdate?: boolean;
  skipRefetch?: boolean;
  isSync?: boolean;
  toggleUpdating?: (_value: boolean) => void;
  rejectCallback?: (value) => void;
  actor?: Actor;
  refetchGetData?: boolean;
}
export interface UpdateTaskActionPayload extends FetchTaskActionPayload {
  data;
  options?: UpdateTaskActionPayloadOptions;
}

export interface ResetEntityDatesActionPayload {
  projectId: number;
  entityId: number;
  data: {
    startDate: MomentInput;
    dueDate: MomentInput;
  };
  type: EntityType;
}

export interface UpdateSprintActionPayload {
  sprintId: number;
  projectId: number;
  data: {
    startDate: string;
    dueDate: string;
    sprintName: string;
    progress: number;
    fields: FieldValue<FieldValueType>[];
  };
}

export const updateTaskSagaAction =
  createAction<UpdateTaskActionPayload>(UPDATE_TASK_SAGA);

export const updateTaskRecordSagaAction =
  createAction<UpdateTaskActionPayload>(UPDATE_TASK_SAGA);

export const updateSprintSagaAction =
  createAction<UpdateSprintActionPayload>(UPDATE_SPRINT_SAGA);

export const updateTaskRecordWithLatestDataSagaAction =
  createAction<UpdateTaskActionPayload>(UPDATE_TASK_RECORD_WITH_LATEST_DATA);

export const updateTaskTimelineSagaAction =
  createAction<UpdateTaskActionPayload>(UPDATE_TASK_TIMELINE_SAGA);

export const updateTaskDatesSagaAction = createAction<UpdateTaskActionPayload>(
  UPDATE_TASK_DATES_SAGA,
);

export const updateTaskPrivacySagaAction = createAction<{
  taskId: number;
  projectId: number;
  private: boolean;
  options?: { rejectCallback?: (value) => void };
}>('UPDATE_TASK_PRIVACY');

export const fetchTaskSaga =
  createAction<FetchTaskActionPayload>(FETCH_TASK_SAGA);

export const saveTaskDetailSaga = createAction<TaskApi>(SAVE_TASK_SAGA);

export const createTaskSagaAction =
  createAction<CreateTaskActionPayload>(CREATE_TASK_SAGA);

export interface CreateSubTaskActionPayload {
  taskName: string;
  parentTask: number;
  projectId: number;
  taskId: number;
  formTemplateId?: number;
  queryClient: QueryClient;
  updateLocalState?: (newSubTask: TaskApi) => void;
}

export interface CreateMultipleSubTasksActionPayload {
  subtasks: Array<{ taskName: string; taskId: number }>;
  parentTask: number;
  projectId: number;
  queryClient: QueryClient;
}

export interface AddSubTaskActionPayload
  extends Omit<CreateSubTaskActionPayload, 'updateLocalState'> {
  fields: FieldValue<number>[];
}

export interface AddMultipleSubtasksActionPayload
  extends CreateMultipleSubTasksActionPayload {
  fields: FieldValue<number>[];
}

export interface CreateSubTaskSuccessActionPayload {
  parentTask: number;
  projectId: number;
  taskId: number;
  task: TaskApi;
}

function withPayloadType() {
  return (t: Omit<CreateSubTaskActionPayload, 'taskId'>) => ({
    payload: { ...t, taskId: moment().valueOf() },
  });
}

function withMutipleSubTasksPayloadType() {
  return (
    t: Omit<CreateMultipleSubTasksActionPayload, 'subtasks'> & {
      subtasks: Array<{ taskName: string }>;
    },
  ) => ({
    payload: {
      ...t,
      subtasks: t.subtasks.map((subtask, index) => ({
        taskName: subtask.taskName,
        taskId: moment().valueOf() + index,
      })),
    },
  });
}

export const createSubTaskSagaAction = createAction(
  CREATE_SUB_TASK_SAGA,
  withPayloadType(),
);

export const createMultipleSubTasksSagaAction = createAction(
  CREATE_MULTIPLE_SUB_TASKS_SAGA,
  withMutipleSubTasksPayloadType(),
);

export interface DeleteTaskActionPayload extends FetchTaskActionPayload {
  options?: UpdateTaskActionPayloadOptions;
}

export const deleteTaskSagaAction =
  createAction<DeleteTaskActionPayload>(DELETE_TASK_SAGA);

export interface DeleteSubTaskActionPayload extends FetchTaskActionPayload {
  taskId: number;
  projectId: number;
  parentTaskId: number;
}

export const deleteSubTaskSagaAction =
  createAction<DeleteSubTaskActionPayload>(DELETE_SUB_TASK_SAGA);

export interface RemoveAsSubtask extends FetchTaskActionPayload {
  queryClient: QueryClient;
  parentTaskId: number | undefined;
  phaseId?: number;
}

export const removeAsSubtaskSagaAction =
  createAction<RemoveAsSubtask>(REMOVE_AS_SUBTASK);

export interface RemoveAsSubtaskSuccess {
  phaseId: number;
  taskId: number;
  projectId: number;
}

export const removeAsSubtaskSuccessAction =
  createAction<RemoveAsSubtaskSuccess>(REMOVE_AS_SUBTASK_SUCCESS);

export interface CreateTaskSuccessPayload {
  projectId: number;
  tempTaskId: number;
  task: TaskApi;
}

export interface CreateMultipleTasksSuccessPayload {
  projectId: number;
  subtasks: Array<{ taskName: string; taskId: number }>;
  tasks: TaskApi[];
}

export interface CreateDependencyActionPayload {
  taskId: number;
  projectId: number;
  dependentTaskId: number;
  canAssignDates: boolean;
  taskUpdates?: DateShiftUpdates;
  isSync?: boolean;
  toggleUpdating?: (_value: boolean) => void;
  handleNullDates?: boolean;
  alignPhaseDates?: boolean;
  isDatesPresent?: boolean;
}

export const createDependencySagaAction =
  createAction<CreateDependencyActionPayload>(CREATE_DEPENDENCY_SAGA);

export const dependencyCreatedAction =
  createAction<CreateDependencyActionPayload>(DEPENDENCY_CREATED_SAGA);

export const dependencyRemovedAction = createAction<
  Omit<
    CreateDependencyActionPayload,
    'projectId' | 'taskUpdates' | 'alignPhaseDates'
  >
>(DEPENDENCY_REMOVED_SAGA);

export const dependenciesRemovedAction = createAction<{
  taskId: number;
  dependentTaskIds: number[];
}>(DEPENDENCIES_REMOVED_SAGA);

export const resetEntityDatesAction =
  createAction<ResetEntityDatesActionPayload>(RESET_ENTITY_DATES);
